import fetchWithAuth from "@/api/secureFetch";
import type {
    ContextMeshCategoryAnalysis,
    ContextMeshCategoryBand,
    ContextMeshCategoryKey,
    ContextMeshCategoryScores,
    ContextMeshTier
} from "../meshStrength/contextMeshAnalysis";
import { parseCategoryAnalysis } from "../meshStrength/contextMeshAnalysis";

export type DraftContextMeshStatus =
    | "ready"
    | "pending"
    | "failed"
    | "baseline_incomplete"
    | "sources_pending";

export type DraftContextMeshResultSource = "current_ready" | "last_ready" | "none" | "pending";

export type DraftContextMeshAnalysisStage =
    | "queued"
    | "queued_for_capacity"
    | "waiting_sources"
    | "freezing_inputs"
    | "preparing_shards"
    | "computing"
    | "verifying"
    | "aggregating"
    | "publishing";

export interface DraftContextMeshMatrixProgress {
    runId: string;
    status?: string;
    stage?: DraftContextMeshAnalysisStage;
    waitingForCapacity?: boolean;
    capacityReason?: string;
    queuedSince?: string;
    capacityWaitSeconds?: number;
    phaseTimings?: Record<string, number>;
}

export interface DraftContextMeshJob {
    jobId: string;
    status: string;
    stage?: string;
    message?: string;
    createdAt?: string;
    updatedAt?: string;
    matrixProgress?: DraftContextMeshMatrixProgress;
}

export interface DraftCanonicalContextMeshAnalysis {
    tier: ContextMeshTier;
    score: number;
    readinessScore: number;
    qualityScore: number;
    categoryScores: ContextMeshCategoryScores;
    categoryAnalysis: ContextMeshCategoryAnalysis | null;
    rubricVersion: string;
    recommendations: string[];
}

export interface DraftContextMeshResponse {
    draftId: number;
    currentDraftVersion: number;
    analyzedDraftVersion: number | null;
    status: DraftContextMeshStatus;
    resultSource: DraftContextMeshResultSource;
    isStale: boolean;
    analysis: DraftCanonicalContextMeshAnalysis | null;
    reason?: string;
    requestId?: string;
    job?: DraftContextMeshJob;
}

export function isExactReadyDraftContextMesh(
    response: DraftContextMeshResponse,
    draftId: number,
    draftVersion: number
): boolean {
    return response.draftId === draftId
        && response.currentDraftVersion === draftVersion
        && response.analyzedDraftVersion === draftVersion
        && response.status === "ready"
        && response.resultSource === "current_ready"
        && !response.isStale
        && response.analysis !== null;
}

export class DraftContextMeshApiError extends Error {
    readonly status: number;
    readonly code: string;

    constructor(status: number, code: string, message: string) {
        super(message);
        this.name = "DraftContextMeshApiError";
        this.status = status;
        this.code = code;
    }
}

const CATEGORY_KEYS: ContextMeshCategoryKey[] = ["discover", "external", "file", "code"];
const CATEGORY_BANDS: ContextMeshCategoryBand[] = ["STRONG", "GOOD", "THIN", "WEAK"];
const TIERS: ContextMeshTier[] = ["GOOD", "BETTER", "BEST"];
const STATUSES: DraftContextMeshStatus[] = ["ready", "pending", "failed", "baseline_incomplete", "sources_pending"];
const RESULT_SOURCES: DraftContextMeshResultSource[] = ["current_ready", "last_ready", "none", "pending"];
const ANALYSIS_STAGES: DraftContextMeshAnalysisStage[] = [
    "queued", "queued_for_capacity", "waiting_sources", "freezing_inputs", "preparing_shards",
    "computing", "verifying", "aggregating", "publishing"
];
const ACTIVE_MATRIX_STATUSES = ["queued", "freezing_inputs", "preparing_shards", "computing", "verifying", "aggregating", "publishing"];
const CAPACITY_REASONS = ["preparation_capacity", "database_capacity", "host_memory_capacity", "cleanup_pending", "coordinator_capacity"];
const PUBLIC_PHASE_TIMINGS = [
    "preparation", "scheduling_source_checks", "compute_staging", "verification", "aggregation_publication",
    "snapshot_read_seconds", "snapshot_freeze_seconds", "shard_planning_seconds", "verification_seconds",
    "aggregation_and_seal_seconds", "source_probe_seconds", "publication_seconds", "source_wait_seconds",
    "claim_wait_seconds", "stage_wait_seconds"
];

function analysisStage(value: unknown): DraftContextMeshAnalysisStage | undefined {
    return typeof value === "string" && ANALYSIS_STAGES.includes(value as DraftContextMeshAnalysisStage)
        ? value as DraftContextMeshAnalysisStage : undefined;
}

function progressDuration(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
}

function parseMatrixProgress(value: unknown): DraftContextMeshMatrixProgress | undefined {
    if (!isRecord(value) || typeof value.runId !== "string" || !value.runId.trim()) return undefined;
    const active = typeof value.status === "string" && ACTIVE_MATRIX_STATUSES.includes(value.status);
    const waiting = active && value.waitingForCapacity === true;
    return {
        runId: value.runId,
        status: typeof value.status === "string" ? value.status : undefined,
        stage: analysisStage(value.stage),
        waitingForCapacity: typeof value.waitingForCapacity === "boolean" ? waiting : undefined,
        capacityReason: waiting && typeof value.capacityReason === "string" && CAPACITY_REASONS.includes(value.capacityReason)
            ? value.capacityReason : undefined,
        queuedSince: waiting && typeof value.queuedSince === "string" && value.queuedSince.length <= 64
            && value.queuedSince.includes("T") && Number.isFinite(Date.parse(value.queuedSince)) ? value.queuedSince : undefined,
        capacityWaitSeconds: waiting ? progressDuration(value.capacityWaitSeconds) : undefined,
        phaseTimings: isRecord(value.phaseTimings)
            ? Object.fromEntries(Object.entries(value.phaseTimings)
                .filter(([name, seconds]) => PUBLIC_PHASE_TIMINGS.includes(name) && progressDuration(seconds) !== undefined)) as Record<string, number>
            : undefined
    };
}

export function draftContextMeshStageMessage(stage: DraftContextMeshAnalysisStage | undefined): string | undefined {
    switch (stage) {
        case "queued": return "Context analysis is queued…";
        case "queued_for_capacity": return "Waiting for analysis capacity…";
        case "waiting_sources": return "Waiting for source processing…";
        case "freezing_inputs":
        case "preparing_shards": return "Preparing context analysis…";
        case "computing": return "Analyzing context…";
        case "verifying": return "Verifying context analysis…";
        case "aggregating": return "Finalizing context score…";
        case "publishing": return "Publishing context score…";
        default: return undefined;
    }
}

export function draftContextMeshProgress(response: DraftContextMeshResponse): {
    analysisStage?: DraftContextMeshAnalysisStage;
    analysisMessage?: string;
} {
    if (response.status === "sources_pending") {
        return { analysisStage: "waiting_sources", analysisMessage: draftContextMeshStageMessage("waiting_sources") };
    }
    if (response.status !== "pending" || !response.job) return {};
    if (response.job.status === "waiting_sources") {
        return { analysisStage: "waiting_sources", analysisMessage: draftContextMeshStageMessage("waiting_sources") };
    }
    if (!["queued", "running", "waiting_vectors"].includes(response.job.status)) return {};
    const matrix = response.job.matrixProgress;
    if (matrix && (!matrix.status || !ACTIVE_MATRIX_STATUSES.includes(matrix.status))) return {};
    const stage = matrix?.waitingForCapacity ? "queued_for_capacity"
        : matrix?.stage ?? analysisStage(response.job.stage);
    return { analysisStage: stage, analysisMessage: draftContextMeshStageMessage(stage) };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function finiteScore(value: unknown, field: string): number {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 100) {
        throw new Error(`Invalid draft Context Mesh response: ${field}`);
    }
    return value;
}

function integer(value: unknown, field: string): number {
    if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
        throw new Error(`Invalid draft Context Mesh response: ${field}`);
    }
    return value;
}

function parseCategories(value: unknown): ContextMeshCategoryScores {
    if (!isRecord(value)) throw new Error("Invalid draft Context Mesh response: categoryScores");
    return Object.fromEntries(
        CATEGORY_KEYS.map(key => {
            const row = value[key];
            if (!isRecord(row) || !CATEGORY_BANDS.includes(row.band as ContextMeshCategoryBand) || typeof row.summary !== "string") {
                throw new Error(`Invalid draft Context Mesh response: categoryScores.${key}`);
            }
            return [key, { score: finiteScore(row.score, `categoryScores.${key}.score`), band: row.band, summary: row.summary }];
        })
    ) as ContextMeshCategoryScores;
}

function parseAnalysis(value: unknown): DraftCanonicalContextMeshAnalysis | null {
    if (value == null) return null;
    if (!isRecord(value) || !TIERS.includes(value.tier as ContextMeshTier) || typeof value.rubricVersion !== "string") {
        throw new Error("Invalid draft Context Mesh response: analysis");
    }
    return {
        tier: value.tier as ContextMeshTier,
        score: finiteScore(value.score, "analysis.score"),
        readinessScore: finiteScore(value.readinessScore, "analysis.readinessScore"),
        qualityScore: finiteScore(value.qualityScore, "analysis.qualityScore"),
        categoryScores: parseCategories(value.categoryScores),
        categoryAnalysis: parseCategoryAnalysis(value.categoryAnalysis),
        rubricVersion: value.rubricVersion,
        recommendations: Array.isArray(value.recommendations)
            ? value.recommendations.filter((item): item is string => typeof item === "string")
            : []
    };
}

export function parseDraftContextMeshResponse(value: unknown): DraftContextMeshResponse {
    if (!isRecord(value)) throw new Error("Invalid draft Context Mesh response");
    const status = value.status as DraftContextMeshStatus;
    const resultSource = value.resultSource as DraftContextMeshResultSource;
    if (!STATUSES.includes(status) || !RESULT_SOURCES.includes(resultSource) || typeof value.isStale !== "boolean") {
        throw new Error("Invalid draft Context Mesh response: provenance");
    }
    const analyzedDraftVersion = value.analyzedDraftVersion == null
        ? null
        : integer(value.analyzedDraftVersion, "analyzedDraftVersion");
    const analysis = parseAnalysis(value.analysis);
    if ((analysis === null) !== (resultSource === "none" || resultSource === "pending") ||
        (analysis !== null && analyzedDraftVersion === null)) {
        throw new Error("Invalid draft Context Mesh response: analysis provenance");
    }
    if (resultSource === "pending" && (analysis !== null || !["pending", "sources_pending"].includes(status))) {
        throw new Error("Invalid draft Context Mesh response: pending provenance");
    }
    const currentDraftVersion = integer(value.currentDraftVersion, "currentDraftVersion");
    if (resultSource === "current_ready" && (status !== "ready" || value.isStale || analyzedDraftVersion !== currentDraftVersion)) {
        throw new Error("Invalid draft Context Mesh response: current result provenance");
    }
    // A configuration/rubric/model change can make the last canonical bundle
    // stale without changing the persisted draft version. Equality is valid in
    // that case; only a result from a future draft version is contradictory.
    if (resultSource === "last_ready" && (!value.isStale || analyzedDraftVersion === null || analyzedDraftVersion > currentDraftVersion || status === "ready")) {
        throw new Error("Invalid draft Context Mesh response: retained result provenance");
    }
    return {
        draftId: integer(value.draftId, "draftId"),
        currentDraftVersion,
        analyzedDraftVersion,
        status,
        resultSource,
        isStale: value.isStale,
        analysis,
        reason: typeof value.reason === "string" ? value.reason : undefined,
        requestId: typeof value.requestId === "string" ? value.requestId : undefined,
        job: isRecord(value.job) && typeof value.job.jobId === "string" && typeof value.job.status === "string"
            ? {
                jobId: value.job.jobId,
                status: value.job.status,
                stage: typeof value.job.stage === "string" ? value.job.stage : undefined,
                message: typeof value.job.message === "string" ? value.job.message : undefined,
                createdAt: typeof value.job.createdAt === "string" ? value.job.createdAt : undefined,
                updatedAt: typeof value.job.updatedAt === "string" ? value.job.updatedAt : undefined,
                matrixProgress: parseMatrixProgress(value.job.matrixProgress)
            }
            : undefined
    };
}

async function readResponse(response: Response): Promise<DraftContextMeshResponse> {
    let payload: unknown;
    try {
        payload = await response.json();
    } catch {
        throw new DraftContextMeshApiError(response.status || 503, "INVALID_RESPONSE", "The Context Mesh service returned an invalid response.");
    }
    if (!response.ok) {
        const error = isRecord(payload) && isRecord(payload.error) ? payload.error : payload;
        const stringError = isRecord(payload) && typeof payload.error === "string" ? payload.error : null;
        throw new DraftContextMeshApiError(
            response.status,
            isRecord(error) && typeof error.code === "string" ? error.code : "ANALYSIS_UNAVAILABLE",
            isRecord(error) && typeof error.message === "string"
                ? error.message
                : stringError ?? "Context analysis is temporarily unavailable."
        );
    }
    try {
        return parseDraftContextMeshResponse(payload);
    } catch {
        throw new DraftContextMeshApiError(503, "INVALID_RESPONSE", "The Context Mesh service returned an invalid response.");
    }
}

export async function scheduleDraftContextMeshAnalysis(
    token: string,
    draftId: number,
    draftVersion: number
): Promise<DraftContextMeshResponse> {
    try {
        return await readResponse(await fetchWithAuth(`/api/product-creation-draft/${draftId}/context-analysis`, token, {
            method: "POST",
            body: JSON.stringify({ draftVersion })
        }));
    } catch (error) {
        if (error instanceof DraftContextMeshApiError) throw error;
        throw new DraftContextMeshApiError(503, "ANALYSIS_UNAVAILABLE", "Context analysis is temporarily unavailable.");
    }
}

export async function getDraftContextMeshAnalysis(token: string, draftId: number): Promise<DraftContextMeshResponse> {
    try {
        return await readResponse(await fetchWithAuth(`/api/product-creation-draft/${draftId}/context-analysis`, token, {
            headers: { Accept: "application/json" }
        }));
    } catch (error) {
        if (error instanceof DraftContextMeshApiError) throw error;
        throw new DraftContextMeshApiError(503, "ANALYSIS_UNAVAILABLE", "Context analysis is temporarily unavailable.");
    }
}
